import './morningBrief.css';
// Morning brief panel (docs/HANDOFF-morning-brief-ui.md, mockups
// docs/2026-10-12-morning-brief-mockups.html M1–M4). Sits between the
// greeting and Needs attention. Renders latest_ok only; when there has never
// been a good brief it renders nothing at all (M3-C). The section renderer is
// exported so the Automations "Recent briefs" modal shows past briefs the
// same way, read-only.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AccordionGroup } from '../../components/AccordionGroup';
import { formatMoney } from '../../components/primitives';
import { api } from '../../lib/api';
import { useIsMobile } from '../../lib/useIsMobile';
import { calendarMonthName } from '../../../shared/dates';
import { briefClock, briefDayLabel, briefItemCount, briefLinkHref, briefModelLabel, briefWeekdayName } from '../../lib/morningBrief';
import {
  BRIEF_SECTION_KEYS,
  type BriefItem,
  type BriefSection,
  type BriefSectionKey,
  type BriefVote,
  type MorningBriefRow,
  type OverviewMorningBrief,
} from '../../../shared/morningBrief';

export const BRIEF_COLLAPSED_KEY = 'ss-os-brief-collapsed';

function readCollapsed(): string | null {
  try {
    return localStorage.getItem(BRIEF_COLLAPSED_KEY);
  } catch {
    return null;
  }
}

function writeCollapsed(briefDate: string | null) {
  try {
    if (briefDate) localStorage.setItem(BRIEF_COLLAPSED_KEY, briefDate);
    else localStorage.removeItem(BRIEF_COLLAPSED_KEY);
  } catch {
    // Collapse still works for this page-load; it just won't persist.
  }
}

function sectionOf(row: MorningBriefRow, key: BriefSectionKey): BriefSection {
  const section = row.content?.sections?.[key];
  return {
    count_label: typeof section?.count_label === 'string' ? section.count_label : undefined,
    items: Array.isArray(section?.items) ? section.items : [],
  };
}

function sectionLabel(row: MorningBriefRow, key: BriefSectionKey, short: boolean): string {
  switch (key) {
    case 'since': return row.content?.since_label || 'Since yesterday';
    case 'week_ahead': return 'Week ahead';
    case 'chase': return 'Chase';
    case 'sitting': return 'Been sitting';
    case 'pipeline': return short ? 'Pipeline' : `Pipeline · ${calendarMonthName(row.brief_date.slice(0, 7))}`;
  }
}

/** The count beside a section label. "3 overdue" segments, and any $ figure
    in Chase, read in danger as mocked. */
function CountLabel({ label, sectionKey }: { label: string | undefined; sectionKey: BriefSectionKey }) {
  if (!label) return null;
  if (sectionKey === 'chase' && label.includes('$')) return <span className="n bad">{label}</span>;
  const parts = label.split(' · ');
  return (
    <span className="n">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          {/overdue/i.test(part) ? <span className="bad">{part}</span> : part}
        </span>
      ))}
    </span>
  );
}

/** The item's whole text is the link target (the model does not mark which
    words are the entity name); the server-attached amount follows in gold. */
function ItemText({ item }: { item: Pick<BriefItem, 'text' | 'link' | 'amount_cents'> }) {
  const href = briefLinkHref(item.link);
  const amount = typeof item.amount_cents === 'number' && Number.isFinite(item.amount_cents)
    ? <> <span className="m">{formatMoney(item.amount_cents)}</span></>
    : null;
  if (!href) return <>{item.text}{amount}</>;
  return (
    <Link className="brief-link" to={href}>
      <span className="ent">{item.text}</span>
      {amount}
    </Link>
  );
}

function ItemList({ items }: { items: BriefItem[] }) {
  if (items.length === 0) return <p className="brief-empty">Nothing here.</p>;
  return (
    <ul>
      {items.map((item, i) => {
        if (!item || typeof item.text !== 'string') return null;
        const cls = [item.pattern ? 'pattern' : item.tone, item.link && briefLinkHref(item.link) ? 'linked' : null]
          .filter(Boolean).join(' ');
        return <li key={i} className={cls || undefined}><ItemText item={item} /></li>;
      })}
    </ul>
  );
}

function OneThing({ row, mobile }: { row: MorningBriefRow; mobile: boolean }) {
  const one = row.content?.one_thing;
  if (!one || typeof one.text !== 'string') return null;
  return (
    <div className={`brief-one${mobile ? ' stacked' : ''}`}>
      <span className="k">One thing</span>
      <span className="t"><ItemText item={one} /></span>
    </div>
  );
}

/** Headline-less body: five sections then One thing (desktop grid), or One
    thing then five accordions (phone). Shared with the Automations history
    modal, which passes `mobile` from its own viewport. */
export function BriefSections({ row, mobile, slugPrefix = 'morning-brief-' }: {
  row: MorningBriefRow;
  mobile: boolean;
  /** AccordionGroup storage slug prefix; the history modal passes its own so
      opening a past brief's sections never changes the Overview panel's. */
  slugPrefix?: string;
}) {
  if (mobile) {
    return (
      <>
        <OneThing row={row} mobile />
        <div className="brief-accs">
          {BRIEF_SECTION_KEYS.map((key) => {
            const section = sectionOf(row, key);
            return (
              <AccordionGroup key={key} slug={`${slugPrefix}${key}`} title={sectionLabel(row, key, true)}
                status={<CountLabel label={section.count_label} sectionKey={key} />} defaultOpen={key === 'since'}>
                <div className="brief-sec"><ItemList items={section.items} /></div>
              </AccordionGroup>
            );
          })}
        </div>
      </>
    );
  }
  return (
    <>
      <div className="brief-grid">
        {BRIEF_SECTION_KEYS.map((key) => {
          const section = sectionOf(row, key);
          return (
            <div key={key} className={`brief-sec${key === 'pipeline' ? ' full' : ''}`} data-section={key}>
              <div className="brief-label">
                {sectionLabel(row, key, false)} <CountLabel label={section.count_label} sectionKey={key} />
              </div>
              <ItemList items={section.items} />
            </div>
          );
        })}
      </div>
      <OneThing row={row} mobile={false} />
    </>
  );
}

function VoteButtons({ vote, pending, onVote }: { vote: BriefVote | null; pending: boolean; onVote: (v: BriefVote) => void }) {
  return (
    <div className="fb" role="group" aria-label="Was this brief useful?">
      <button type="button" className={vote === 'useful' ? 'on' : undefined} aria-pressed={vote === 'useful'}
        disabled={pending} onClick={() => onVote('useful')}>Useful</button>
      <button type="button" className={vote === 'off' ? 'on' : undefined} aria-pressed={vote === 'off'}
        disabled={pending} onClick={() => onVote('off')}>Off</button>
    </div>
  );
}

function RoutineLink({ url, label, className }: { url: string; label: string; className: string }) {
  return <a className={className} href={url} target="_blank" rel="noreferrer">{label}</a>;
}

export function MorningBriefPanel({
  data,
  today,
  onVoted,
}: {
  data: OverviewMorningBrief | null | undefined;
  today: string;
  onVoted?: () => void;
}) {
  const isMobile = useIsMobile();
  const row = data?.latest_ok ?? null;
  const briefDate = row?.brief_date ?? null;

  const [collapsedFor, setCollapsedFor] = useState<string | null>(() => readCollapsed());
  const [vote, setVote] = useState<BriefVote | null>(row?.vote ?? null);
  const [votePending, setVotePending] = useState(false);
  useEffect(() => setVote(row?.vote ?? null), [row?.id, row?.vote]);

  if (!data || !row || !row.content || !briefDate) return null;

  const collapsed = collapsedFor === briefDate;
  const toggleCollapsed = () => {
    const next = collapsed ? null : briefDate;
    setCollapsedFor(next);
    writeCollapsed(next);
  };

  const castVote = (choice: BriefVote) => {
    if (votePending) return;
    const previous = vote;
    const next = previous === choice ? null : choice;
    setVote(next);
    setVotePending(true);
    api(`/api/admin/morning-brief/${briefDate}/vote`, { method: 'POST', json: { vote: next } })
      .then(() => onVoted?.())
      .catch(() => setVote(previous))
      .finally(() => setVotePending(false));
  };

  const stale = briefDate !== today;
  const latest = data.latest;
  const failedToday = !!latest && latest.status === 'failed' && latest.brief_date === today;
  const missedToday = !failedToday && data.missed_today && stale;
  const routineUrl = data.routine_url || null;
  const showingDay = `${briefWeekdayName(briefDate)}'s`;

  const day = briefDayLabel(briefDate);
  const clock = briefClock(row.generated_at);
  const stamp = `${stale ? `${showingDay} brief · ` : ''}${day}${isMobile || !clock ? '' : ` · ${clock}`}`;

  let notice: ReactNode = null;
  if (failedToday && stale) {
    const reported = typeof latest!.error === 'string' && latest!.error
      ? ` (the routine reported: ${latest!.error.replace(/\.$/, '')})` : '';
    const failedAt = briefClock(latest!.generated_at);
    notice = (
      <div className="inline-notice err" role="status">
        <span className="dot" />
        <span className="msg">
          Today's brief failed{failedAt ? ` at ${failedAt}` : ''}{reported}. Showing {showingDay}. Details on Automations.
        </span>
        {routineUrl
          ? <RoutineLink className="act" url={routineUrl} label="Routine ↗" />
          : <Link className="act" to="/automations">Automations →</Link>}
      </div>
    );
  } else if (missedToday) {
    notice = (
      <div className="inline-notice err" role="status">
        <span className="dot" />
        <span className="msg">No brief received today. Showing {showingDay}.</span>
        <Link className="act" to="/automations">Automations →</Link>
      </div>
    );
  }

  const content = row.content;
  const headline = typeof content.headline === 'string' ? content.headline : '';
  const oneThingText = typeof content.one_thing?.text === 'string' ? content.one_thing.text : null;

  return (
    <section className={`brief${collapsed ? ' collapsed' : ''}${isMobile ? ' is-mobile' : ''}`} aria-label="Morning brief">
      <div className="brief-head">
        <span className="brief-eyebrow">Morning brief</span>
        <span className={`brief-when${stale ? ' stale' : ''}`}>{stamp}</span>
        <div className="brief-acts">
          {!isMobile && routineUrl && (
            <RoutineLink className={`brief-act${failedToday ? ' hot' : ''}`} url={routineUrl}
              label={failedToday ? 'Run routine ↗' : 'Routine ↗'} />
          )}
          <button type="button" className="brief-act" aria-expanded={!collapsed} onClick={toggleCollapsed}>
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      </div>
      {notice}
      {collapsed ? (
        <p className="brief-headline">
          {headline}
          {oneThingText && <> <span className="brief-one-inline">One thing: {oneThingText}</span></>}
        </p>
      ) : (
        <>
          <p className="brief-headline">{headline}</p>
          <BriefSections row={row} mobile={isMobile} />
          {isMobile ? (
            <div className="brief-foot">
              <span>{[clock, briefModelLabel(row.model).replace(/^Claude /, '')].filter(Boolean).join(' · ')}</span>
              {routineUrl && <RoutineLink className="brief-foot-link" url={routineUrl} label="Routine ↗" />}
              <VoteButtons vote={vote} pending={votePending} onVote={castVote} />
            </div>
          ) : (
            <div className="brief-foot">
              <span>{[clock ? `Received ${clock}` : 'Received', briefModelLabel(row.model), briefItemCount(row.item_count)].join(' · ')}</span>
              <span className="sep">|</span>
              <span>Reads only OS data</span>
              <VoteButtons vote={vote} pending={votePending} onVote={castVote} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
